import { z } from 'zod';
import { FILTERABLE_COLUMNS, SORTABLE_COLUMNS } from '../constants/columns';

export const uuidSchema = z.string().uuid('Некорректный идентификатор');

/**
 * Дата без времени в формате ISO (гггг-мм-дд).
 * В интерфейсе показывается как дд.мм.гггг, но по проводам ходит ISO —
 * так исключается путаница дд.мм против мм.дд.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ожидается дата в формате гггг-мм-дд')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Такой даты не существует');

export const sortDirectionSchema = z.enum(['asc', 'desc']);

export const sortSchema = z.object({
  field: z.enum(SORTABLE_COLUMNS as [string, ...string[]]),
  dir: sortDirectionSchema.default('asc'),
});
export type Sort = z.infer<typeof sortSchema>;

/** Операторы фильтрации. Набор зависит от типа колонки. */
export const filterOperatorSchema = z.enum([
  'eq',
  'neq',
  'contains',
  'notContains',
  'startsWith',
  'in',
  'notIn',
  'isEmpty',
  'isNotEmpty',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
]);
export type FilterOperator = z.infer<typeof filterOperatorSchema>;

export const filterSchema = z.object({
  field: z.enum(FILTERABLE_COLUMNS as [string, ...string[]]),
  op: filterOperatorSchema,
  /** Для in/notIn — массив, для between — [от, до], иначе скаляр */
  value: z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.union([z.string(), z.number(), z.null()]))]).optional(),
});
export type Filter = z.infer<typeof filterSchema>;

/**
 * Keyset-пагинация. Курсор — непрозрачная для клиента строка,
 * внутри base64 от { значение поля сортировки, id }.
 *
 * Почему не offset: при десятках тысяч строк `OFFSET 40000` заставляет
 * Postgres читать и выбрасывать 40 000 строк на каждый скролл.
 * Плюс при вставке строк другими пользователями offset «съезжает»
 * и одна и та же строка приезжает дважды.
 */
export const cursorSchema = z.string().min(1).max(512);

export const paginationSchema = z.object({
  cursor: cursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  /** Направление подгрузки: вперёд при скролле вниз, назад — вверх */
  direction: z.enum(['forward', 'backward']).default('forward'),
});

export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    prevCursor: z.string().nullable(),
    /** Приблизительное общее число строк — для полосы прокрутки */
    totalEstimate: z.number().int().nonnegative().nullable(),
  });
}

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
  prevCursor: string | null;
  totalEstimate: number | null;
}

/**
 * Идентификатор клиентской операции. Нужен для двух вещей:
 *  1. идемпотентность — повтор запроса после обрыва сети не применяется дважды;
 *  2. realtime — клиент узнаёт собственное эхо и не мигает лишний раз.
 */
export const clientOpIdSchema = z.string().min(8).max(64);

export const okSchema = z.object({ ok: z.literal(true) });
